import { useMutation } from "@tanstack/react-query";
import { configureAuth } from "react-query-auth";
import { z } from "zod/v4";

import { global_settings } from "@/config";
import { env } from "@/config/env";
import { api } from "@/lib/api-client";
import { refreshAccessToken, removeAccessToken, setAccessToken } from "@/lib/auth";

import type { AuthResponse } from "@/types/auth";
import type { User } from "@/types/user";

export const getUser = async (): Promise<User | null> => {
  try {
    return (await api.get("/users/me/")) as User;
  } catch (err) {
    return null;
  }
};

export const logout = (): Promise<void> => {
  return api.post("/auth/logout/");
};

export const loginInputSchema = z.object({
  username: z.string().nonempty(),
  password: z.string().nonempty(),
});

export type LoginInput = z.infer<typeof loginInputSchema>;

export const loginWithUsernameAndPassword = ({
  username,
  password,
}: LoginInput): Promise<AuthResponse> => {
  return api.post("/auth/login/", { hostname: env.HOST, username, password });
};

export const signUpInputSchema = z.object({
  name: z.string().nonempty(),
  email: z.email(),
});

export type SignUpInput = z.infer<typeof signUpInputSchema>;

export const signUpWithUsernameAndPassword = ({ name, email }: SignUpInput): Promise<User> => {
  return api.post("/carburate/signup/", { name, email });
};

export const activateAccountSchema = z.object({
  activation_token: z.uuid(),
  new_password: z.string().min(8),
});

export type ActivateAccountInput = z.infer<typeof activateAccountSchema>;

export const activateAccount = ({
  activation_token,
  new_password,
}: ActivateAccountInput): Promise<void> => {
  return api.post("/signup/activation/", {
    activation_token,
    new_password,
  });
};

export const passwordRecoverCreateTokenSchema = z.object({
  username: z.email(),
});

export type passwordRecoverCreateTokenInput = z.infer<typeof passwordRecoverCreateTokenSchema>;

export const createpasswordRecoverToken = async ({
  username,
}: passwordRecoverCreateTokenInput): Promise<void> => {
  return api.post("/auth/password_recovery/create_token/", { username });
};

export const passwordRecoverSetPasswordSchema = z.object({
  recovery_token: z.string(),
  new_password: z.string().min(8),
});

export type passwordRecoverSetPasswordInput = z.infer<typeof passwordRecoverSetPasswordSchema>;

export const setPasswordWithRecoveryToken = async ({
  recovery_token,
  new_password,
}: passwordRecoverSetPasswordInput): Promise<void> => {
  const response = await fetch(`${env.API_URL}/auth/password_recovery/set_password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${recovery_token}`,
    },
    body: JSON.stringify({
      new_password,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Request failed" }));
    throw new Error(error.message || "Failed to reset password");
  }
};

export const getRecoveryTokenFromUrl = (): string | null => {
  if (typeof window === "undefined") return null;

  const segments = window.location.pathname.split("/");
  const lastSegment = segments[segments.length - 1];

  const token = lastSegment.split("?")[0];

  return token || null;
};

export const setPasswordFromRecoveryPage = async ({
  new_password,
}: {
  new_password: string;
}): Promise<void> => {
  const recovery_token = getRecoveryTokenFromUrl();

  if (!recovery_token) {
    throw new Error("Recovery token not found in URL");
  }

  const response = await fetch(`${env.API_URL}/auth/password_recovery/set_password`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      recovery_token,
      new_password,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Request failed" }));
    throw new Error(error.message || "Failed to reset password");
  }
};

function setUSDExchangeRate(user: User | null) {
  localStorage.setItem(
    "exchangeRate",
    String(user?.additional_info.currency?.value ?? global_settings.defaultExchangeRate)
  );
}

const authConfig = {
  userFn: getUser,
  loginFn: async (data: LoginInput) => {
    const response = await loginWithUsernameAndPassword(data);
    setAccessToken(response);
    const user = await getUser();
    setUSDExchangeRate(user);
    return user;
  },
  registerFn: async (data: SignUpInput) => {
    await signUpWithUsernameAndPassword(data);
    return { user: null } as any;
  },
  refreshFn: async () => {
    await refreshAccessToken();
    const user = await getUser();
    return user;
  },
  logoutFn: async () => {
    return logout().then(() => {
      removeAccessToken();
    });
  },
};

export const { useUser, useLogin, useRegister, useLogout, AuthLoader } = configureAuth(authConfig);

export function useActivate() {
  return useMutation({
    mutationFn: (data: ActivateAccountInput) => activateAccount(data),
  });
}

export function useRequestpasswordRecover() {
  return useMutation({
    mutationFn: (data: passwordRecoverCreateTokenInput) => createpasswordRecoverToken(data),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (data: passwordRecoverSetPasswordInput) => setPasswordWithRecoveryToken(data),
  });
}

export function useSetPasswordFromRecoveryPage() {
  return useMutation({
    mutationFn: (data: { new_password: string }) => setPasswordFromRecoveryPage(data),
  });
}
